import styled from 'styled-components';

import { Spinner } from 'ui/spinner';
import { spacing, easing, duration } from 'ui/theme';

import { ListItem, ListItemPending } from '../AssetListItems';
import { useAssetsQueryContext } from '../../contexts/AssetsQueryContext';
import { useUploadAssetsContext } from '../../contexts/UploadAssetsContext';
import { usePanelContext } from '../../contexts/Panel';
import { Asset, PanelView } from '../../types';
import { ScrollableListContainer } from './ScrollableListContainer';
import { useDeleteImageOverlayContext } from 'modules/creativeLibrary/contexts/DeleteImageOverlayContext';

const LoadingContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: ${spacing.base};
  transition: all ${duration.short} ${easing.entrance};
`;

// displays a list of assets from the creative library
export const AssetList = () => {
  const {
    assetCount,
    assetCountTotal,
    assets,
    isLoading: isFetchingAssets,
  } = useAssetsQueryContext();
  const { setPanelView, setSelectedAsset } = usePanelContext();

  const handleSelectAsset = (asset: Asset) => {
    setSelectedAsset(asset);
    setPanelView(PanelView.Detail);
  };

  return !assetCount ? null : (
    <ScrollableListContainer>
      {assets.map((asset) => (
        <ListItem
          asset={asset}
          key={`asset_${asset.id}`}
          onClick={() => handleSelectAsset(asset)}
        />
      ))}
      {assetCount < assetCountTotal && (
        <LoadingContainer style={{ opacity: isFetchingAssets ? 1 : 0 }}>
          <Spinner />
        </LoadingContainer>
      )}
    </ScrollableListContainer>
  );
};

// displays a list of pending uploads (ready to upload, uploading, successful upload, or error)
export const AssetListPending = () => {
  const { pendingImages } = useUploadAssetsContext();
  const { setPanelView, setSelectedAsset } = usePanelContext();
  const { setImgsIdsToDelete } = useDeleteImageOverlayContext();

  const handleSelectAsset = (asset?: Asset) => {
    if (asset) {
      setSelectedAsset(asset);
      setPanelView(PanelView.Detail);
    }
  };

  // completed uploads will have the .asset key defined (the api return of a successful upload)
  // this allows successful uploads to be selected from the upload view

  return !pendingImages.length ? null : (
    <>
      {pendingImages.map((pendingAsset) =>
        pendingAsset.asset ? (
          <ListItem
            onDelete={() => setImgsIdsToDelete([pendingAsset.id])}
            asset={pendingAsset.asset}
            key={`asset_${pendingAsset.asset.id}`}
            onClick={() => handleSelectAsset(pendingAsset.asset)}
          />
        ) : (
          <ListItemPending
            image={pendingAsset}
            key={`pending_${pendingAsset.id}`}
            onDelete={() => setImgsIdsToDelete([pendingAsset.id])}
          />
        ),
      )}
    </>
  );
};
